import { useEffect, useState } from 'react';
import { Button, Form, Message, Modal } from 'semantic-ui-react';
import './CreateFragmentModal.scss';
import Fragment, { FragmentType } from '../../../../../../structures/fragment';
import Project from '../../../../../../structures/project';
import api, { baseURL, TitleAndTextResponse } from '../../../../../../utils/api';
import ExamplesGenerator from '../../../../../../shared/ExamplesGenerator/ExamplesGenerator';

interface CreateFragmentModalProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    project: Project;
    partId: string;
    onSave: (project: Fragment) => void;
}

function CreateFragmentModal({open, setOpen, project, partId, onSave}:CreateFragmentModalProps) {
    const [type, setType] = useState<FragmentType>()
    const [description, setDescription] = useState('')
    const [members, setMembers] = useState<string[]>([])
    const [hero, setHero] = useState<string|undefined>()
    const [place, setPlace] = useState<string|undefined>()
    const [objects, setObjects] = useState<string[]>([])

    useEffect(()=>{
        setDescription('');
        setMembers([]);
        setHero(undefined);
        setPlace(undefined);
        setObjects([]);
    }, [type]);
    useEffect(()=>{
        if(open){
            setType(undefined);
            setType(undefined);
            setDescription('');
            setMembers([]);
            setHero(undefined);
            setPlace(undefined);
            setObjects([]);
        }
    }, [open]);

    if(project === undefined)
        return ''

    const canCreate = ()=>{
        if(type === FragmentType.Dialog && members.length > 0 && place !== undefined && description !== '') return true;
        if(type === FragmentType.HeroDescription && hero !== undefined && description !== '') return true;
        if(type === FragmentType.PlaceDescription &&  place !== undefined && description !== '') return true;
        if(type === FragmentType.ActionDescription && members.length > 0 && description !== '') return true;
        if(type === FragmentType.ReflectionsOrReasoning && description !== '') return true;
        return false;
    }
    const create = async () => {
        let fragment = await api.createFragment(project.id, partId, type!, description, members, place !== undefined ? [place] : [], objects)
        onSave(fragment)
        setOpen(false);
    };
    return <Modal
        open={open}
        onClose={() => setOpen(false)}
        className='create-fragment-modal'
        centered={false}
        size='tiny'
        closeIcon
        closeOnDimmerClick={false}
    >
        <Modal.Header>Новый фрагмент</Modal.Header>
        <Modal.Content>
            <Form>
                <Form.Dropdown 
                    selection 
                    label='Тип' 
                    placeholder='Выберите тип фрагмента...'
                    options={[FragmentType.Dialog, FragmentType.HeroDescription, FragmentType.PlaceDescription, FragmentType.ReflectionsOrReasoning, FragmentType.ActionDescription].map(x=> ({key: x, text: x, value: x}))}
                    value={type}
                    onChange={(e, {value})=> {setType(value as FragmentType)}}
                    />
                {type === FragmentType.Dialog && <>
                    <Form.Dropdown 
                        selection 
                        multiple 
                        label='Участники диалога'
                        className='multiple-members-selector'
                        options={project.members.map(x=> ({
                            key: x.id, 
                            text: x.name, 
                            value: x.id,
                            image: `${baseURL}/api/generatedImage/${x.imageId}`,
                            description: x.role,
                        }))}
                        value={members}
                        onChange={(e, data) => {setMembers(data.value as string[])}}
                        placeholder='Выберите участников диалога...'
                        />
                    {members.length > 0 ? <Form.Dropdown 
                        selection 
                        label='Место проведения диалога'
                        className='multiple-members-selector'
                        options={project.places.map(x=> ({
                            key: x.id, 
                            text: x.name, 
                            value: x.id,
                            image: `${baseURL}/api/generatedImage/${x.imageId}`,
                        }))}
                        placeholder='Где проходит диалог...'
                        value={place}
                        onChange={(e, data) => setPlace(data.value as string|undefined)}
                        /> : ''}
                    {members.length > 0 && place !== undefined ? <>
                            <Form.TextArea 
                                label='Описание диалога'
                                placeholder='Напишите о чём диалог...'
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                            <ExamplesGenerator<TitleAndTextResponse> 
                                popupDescription='Придумать примеры описаний диалогов'
                                target={description}
                                context={{projectId: project.id, members, place}}
                                apiMethod={api.getDialogDescriptionExamples} 
                                onSelect={x => setDescription(x.text)} 
                                toTextDisplay={x=>x.title}
                                toPopupDisplay={x=>x.text}
                            />
                        </> : ''}
                </>}
                {type === FragmentType.HeroDescription && <>
                    <Form.Dropdown 
                        selection 
                        label='Герой'
                        className='multiple-members-selector'
                        options={project.members.map(x=> ({
                            key: x.id, 
                            text: x.name, 
                            value: x.id,
                            image: `${baseURL}/api/generatedImage/${x.imageId}`,
                            description: x.role,
                        }))}
                        value={hero}
                        onChange={(e, data) => setHero(data.value as string|undefined)}
                        placeholder='Выберите героя для описания...'
                        />
                    {hero !== undefined ? <Message>{project.members.find(x=>x.id === hero)!.context}</Message> : ''}
                    {hero !== undefined ? <Form.TextArea 
                        label='На что сделать акцент'
                        placeholder='Опишите характеристики героя, на которые Вы бы хотели акцентировать сейчас внимание...'
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        /> : ''}
                    {hero !== undefined ? <ExamplesGenerator<TitleAndTextResponse> 
                                popupDescription='Придумать примеры акцентов при описании героя'
                                target={description}
                                context={{projectId: project.id, hero}}
                                apiMethod={api.getHeroDescriptionExamplesForFragment} 
                                onSelect={x => setDescription(x.text)} 
                                toTextDisplay={x=>x.title}
                                toPopupDisplay={x=>x.text}
                            /> : ''}
                </>}
                {type === FragmentType.PlaceDescription && <>
                    <Form.Dropdown 
                        selection 
                        label='Место'
                        className='multiple-members-selector'
                        options={project.places.map(x=> ({
                            key: x.id, 
                            text: x.name, 
                            value: x.id,
                            image: `${baseURL}/api/generatedImage/${x.imageId}`,
                        }))}
                        placeholder='Выберите место...'
                        value={place}
                        onChange={(e, data) => setPlace(data.value as string|undefined)}
                        />
                    {place !== undefined ? <Form.TextArea 
                        label='Описание места'
                        placeholder='Напишите на что сделать акцент при описании места...'
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        /> : ''}
                </>}
                {type === FragmentType.ReflectionsOrReasoning && <>
                    <Form.TextArea 
                        label='Рассуждения'
                        placeholder='Напишите, что Вы бы хотели донести до читателя...'
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        />
                </>}
                {type === FragmentType.ActionDescription && <>
                    <Form.Dropdown 
                        selection 
                        multiple 
                        label='Субъекты действия'
                        className='multiple-members-selector'
                        options={project.members.map(x=> ({
                            key: x.id, 
                            text: x.name, 
                            value: x.id,
                            image: `${baseURL}/api/generatedImage/${x.imageId}`,
                            description: x.role,
                        }))}
                        placeholder='Выберите участников действия...'
                        value={members}
                        onChange={(e, data) => {setMembers(data.value as string[])}}
                        />
                    {members.length > 0 ? <Form.Dropdown 
                        selection 
                        label='Место проведения действия'
                        className='multiple-members-selector'
                        options={project.places.map(x=> ({
                            key: x.id, 
                            text: x.name, 
                            value: x.id,
                            image: `${baseURL}/api/generatedImage/${x.imageId}`,
                        }))}
                        placeholder='Где происходит действие...'
                        value={place}
                        onChange={(e, data) => setPlace(data.value as string|undefined)}
                        /> : ''}
                    {members.length > 0 && place !== undefined ? <Form.Dropdown 
                        selection 
                        multiple
                        label='Объекты, участвующие в действии'
                        className='multiple-members-selector'
                        options={project.objects.map(x=> ({
                            key: x.id, 
                            text: x.name, 
                            value: x.id,
                            image: `${baseURL}/api/generatedImage/${x.imageId}`,
                        }))}
                        placeholder='Какие предметы участвуют в сцене...'
                        value={objects}
                        onChange={(e, data) => setObjects(data.value as string[])}
                        /> : ''}
                        {members.length > 0 && place !== undefined ? <Form.TextArea 
                        label='Описание действия'
                        placeholder='Опишите, что происходит...'
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        /> : ''}
                </>}
            </Form>
        </Modal.Content>
        <Modal.Actions>
            <Button basic onClick={() => setOpen(false)}>Отмена</Button>
            <Button primary onClick={create} disabled={!canCreate()}>Создать</Button>
        </Modal.Actions>
    </Modal>
};

export default CreateFragmentModal;